import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DeliveryDining as DeliveryIcon,
  ElectricBolt as ElectricBoltIcon,
  Hub as HubIcon,
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  Store as StoreIcon,
  TrendingUp as TrendingUpIcon,
  TwoWheeler as ScooterIcon,
} from '@mui/icons-material'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { GoogleMap, OverlayView, useLoadScript } from '@react-google-maps/api'
import { useTheme } from '@mui/material/styles'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { dashboardApi } from '../services/dashboardService'
import { riderService, vehicleService } from '../services'
import clientStoreService from '../services/clientStoreService'
import { cityService, hubService } from '../services/hubService'
import ordersService from '../services/ordersService'
import { useWarRoomSocket } from '../hooks/useWarRoomSocket'
import { ManagementDashboardMetrics, DepartmentType } from '../types/department'
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  getGreeting,
} from '../utils/dashboardHelpers'
import {
  CityMapCenter,
  cityMapCenters,
  indiaDefaultCenter,
  normalizeIndiaCityName,
} from '../utils/indiaCityMap'
import evDashboardImage from '../assets/vehicle-icons/ev-vehicles91-3-removebg-preview.png'


const chartPalette = {
  blue: '#2563eb',
  cyan: '#0891b2',
  emerald: '#059669',
  amber: '#d97706',
  rose: '#e11d48',
  violet: '#7c3aed',
  slate: '#334155',
  grid: 'rgba(148, 163, 184, 0.22)',
}

const lightOperationsMapStyles = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cbd5e1' }, { weight: 1 }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f8fbff' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#eefaf1' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e2e8f0' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c7e9fb' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#2563eb' }] },
]

const darkOperationsMapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#07111f' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#07111f' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#1e3a5f' }, { weight: 1 }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#dbeafe' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#061426' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#08213a' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#0b243d' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#15395a' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#020b16' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
]

type ChartPanelProps = {
  title: string
  subtitle?: string
  icon: ReactNode
  children: ReactNode
  action?: ReactNode
}

function ChartPanel({ title, subtitle, icon, children, action }: ChartPanelProps) {
  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.24)' : 'rgba(148, 163, 184, 0.22)',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(145deg, rgba(15,23,42,0.94), rgba(17,24,39,0.86))'
            : 'linear-gradient(145deg, rgba(255,255,255,0.96), rgba(241,245,249,0.9))',
        color: theme.palette.mode === 'dark' ? '#e5edf8' : '#0f172a',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 22px 46px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255,255,255,0.8)'
            : '0 22px 46px rgba(15, 23, 42, 0.11), inset 0 1px 0 rgba(255,255,255,0.7)',
        overflow: 'hidden',
        position: 'relative',
        transformStyle: 'preserve-3d',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            theme.palette.mode === 'dark'
              ? 'radial-gradient(circle at top right, rgba(34,197,94,0.1), transparent 30%)'
              : 'radial-gradient(circle at top right, rgba(37,99,235,0.1), transparent 30%), radial-gradient(circle at bottom left, rgba(5,150,105,0.08), transparent 34%)',
          pointerEvents: 'none',
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 28px 58px rgba(0, 0, 0, 0.26), inset 0 1px 0 rgba(255,255,255,0.85)'
              : '0 28px 58px rgba(15, 23, 42, 0.15), inset 0 1px 0 rgba(255,255,255,0.8)',
        },
      })}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, position: 'relative', zIndex: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} spacing={1.25} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.4} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                color: (theme) => theme.palette.mode === 'dark' ? '#16a34a' : chartPalette.blue,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(37, 99, 235, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={900} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  )
}

function DonutGauge({
  label,
  value,
  color,
}: {
  label: string
  value: number
  color: string
}) {
  const safeValue = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))
  const isEmpty = safeValue < 0.5
  const data = [
    { name: label, value: safeValue },
    { name: 'Remaining', value: 100 - safeValue },
  ]

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        maxWidth: { xs: 116, sm: 136, md: 148 },
        aspectRatio: '1 / 1',
        mx: 'auto',
        position: 'relative',
        borderRadius: '50%',
        background:
          theme.palette.mode === 'dark'
            ? 'radial-gradient(circle, rgba(15,23,42,0.34) 0%, rgba(15,23,42,0.46) 54%, rgba(15,23,42,0.18) 100%)'
            : 'radial-gradient(circle, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.9) 58%, rgba(226,232,240,0.72) 100%)',
        filter: theme.palette.mode === 'dark'
          ? 'drop-shadow(0 14px 22px rgba(0,0,0,0.18))'
          : 'drop-shadow(0 10px 12px rgba(15,23,42,0.10))',
      })}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            innerRadius="39%"
            outerRadius="48%"
            startAngle={90}
            endAngle={-270}
            stroke="transparent"
            strokeWidth={0}
            paddingAngle={safeValue > 0 && safeValue < 100 ? 1.5 : 0}
          >
            <Cell fill={color} />
            <Cell fill={isEmpty ? 'rgba(100,116,139,0.22)' : 'rgba(100,116,139,0.16)'} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <Box
        sx={(theme) => ({
          position: 'absolute',
          inset: '34%',
          borderRadius: '50%',
          background: theme.palette.mode === 'dark'
            ? 'rgba(15,23,42,0.82)'
            : 'rgba(255,255,255,0.74)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(148,163,184,0.13)'
            : '1px solid rgba(226,232,240,0.7)',
          boxShadow: theme.palette.mode === 'dark'
            ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
            : 'inset 0 1px 0 rgba(255,255,255,0.86)',
          pointerEvents: 'none',
        })}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: '26%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          pointerEvents: 'none',
        }}
      >
        <Typography variant="h6" fontWeight={900} sx={{ color, fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1 }}>
          {safeValue.toFixed(0)}%
        </Typography>
        <Typography
          variant="caption"
          sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? 'rgba(203,213,225,0.74)' : '#64748b',
            px: 0.5,
            lineHeight: 1.08,
            fontSize: { xs: '0.64rem', sm: '0.7rem' },
            maxWidth: 78,
          })}
          textAlign="center"
        >
          {label}
        </Typography>
      </Box>
    </Box>
  )
}

type MetricPrismCardProps = {
  label: string
  value: string | number
  helper: string
  icon: ReactNode
  color: string
  soft: string
  bars: number[]
}

function MetricPrismCard({ label, value, helper, icon, color, soft, bars }: MetricPrismCardProps) {
  const maxBar = Math.max(...bars, 1)

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        height: '100%',
        minHeight: 120,
        borderRadius: 2,
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.22)' : 'rgba(148, 163, 184, 0.22)'}`,
        color: theme.palette.mode === 'dark' ? '#e5edf8' : '#0f172a',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(145deg, rgba(15,23,42,0.92), rgba(17,24,39,0.82))'
          : 'linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,250,252,0.9))',
        backdropFilter: 'blur(16px)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 18px 40px rgba(0, 0, 0, 0.24)'
            : '0 18px 40px rgba(15, 23, 42, 0.1)',
        overflow: 'hidden',
        position: 'relative',
        transformStyle: 'preserve-3d',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderTop: `4px solid ${color}`,
          pointerEvents: 'none',
        },
        '&:after': {
          content: '""',
          position: 'absolute',
          right: -58,
          top: -70,
          width: 170,
          height: 170,
          borderRadius: '50%',
          background: soft,
          opacity: 0.78,
        },
        '&:hover': {
          transform: 'translateY(-4px) rotateX(1deg)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 26px 54px rgba(0, 0, 0, 0.28)'
              : '0 26px 54px rgba(15, 23, 42, 0.15)',
        },
      })}
    >
      <CardContent sx={{ p: { xs: 1.1, md: 1.25 }, position: 'relative', zIndex: 1, height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#475569', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0 }}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={950} sx={{ mt: 0.75, color, lineHeight: 1, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#64748b' }}>
              {helper}
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 34, md: 48 },
              height: { xs: 34, md: 48 },
              borderRadius: 2,
              color,
              background: soft,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 12px 22px ${color}2B`,
              '& svg': { fontSize: 26 },
            }}
          >
            {icon}
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.6} alignItems="flex-end" sx={{ height: 34, mt: 1.25 }}>
          {bars.map((bar, index) => (
            <Box
              key={`${label}-${index}`}
              sx={{
                width: 8,
                height: `${Math.max(8, (bar / maxBar) * 28)}px`,
                borderRadius: '6px 6px 2px 2px',
                background: `linear-gradient(180deg, ${color}, ${soft})`,
                boxShadow: `4px 5px 0 ${color}24`,
                transform: 'skewX(-8deg)',
              }}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

function ThreeDBarScene({
  data,
}: {
  data: Array<{ name: string; value: number; fill: string }>
}) {
  const maxValue = Math.max(...data.map((item) => item.value), 1)

  return (
    <Box
      sx={{
        minHeight: 220,
        p: 1.25,
        borderRadius: 2,
        // background: '#f8fafc',
        perspective: '900px',
      }}
    >
      <Stack direction="row" alignItems="flex-end" justifyContent="space-around" spacing={1} sx={{ height: 180, transform: 'rotateX(8deg)' }}>
        {data.map((item) => (
          <Stack key={item.name} alignItems="center" spacing={1} sx={{ width: `${100 / data.length}%`, minWidth: 58 }}>
            <Box
              sx={{
                width: { xs: 34, sm: 46 },
                height: `${Math.max(20, (item.value / maxValue) * 140)}px`,
                borderRadius: '9px 9px 3px 3px',
                background: `linear-gradient(180deg, ${item.fill}, ${item.fill}B8)`,
                boxShadow: `12px 13px 0 ${item.fill}29, 0 18px 28px rgba(15,23,42,0.18)`,
                transform: 'skewY(-4deg)',
                position: 'relative',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: -9,
                  left: 6,
                  right: -6,
                  height: 10,
                  borderRadius: '8px 8px 0 0',
                  background: `linear-gradient(90deg, ${item.fill}CC, ${item.fill})`,
                  transform: 'skewX(-42deg)',
                },
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  top: 4,
                  right: -10,
                  width: 10,
                  bottom: -8,
                  borderRadius: '0 8px 5px 0',
                  background: `${item.fill}78`,
                  transform: 'skewY(45deg)',
                },
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155', textAlign: 'center' }}>
              {item.name}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 900, color: item.fill }}>
              {formatNumber(item.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

type WarRoomDistanceLeader = {
  label: string
  vehicleId: string
  distanceKm: number
  source: string
}

const getTodayKey = () => new Date().toISOString().split('T')[0]

const normalizeApiBase = (url?: string) => {
  const fallback = 'http://localhost:8000'
  const base = (url || fallback).replace(/\/$/, '')
  return base.endsWith('/api') ? base.slice(0, -4) : base
}

const formatVehicleLabel = (vehicleId: string) => vehicleId?.split(':').pop() || vehicleId || 'NA'

function WarRoomPulseTable({
  stats,
  leaders,
  loading,
}: {
  stats: {
    total: number
    opspod: number
    altmobility: number
    moving: number
    idle: number
    offline: number
  }
  leaders: WarRoomDistanceLeader[]
  loading: boolean
}) {
  const statusItems = [
    { label: 'Moving', value: stats.moving, color: chartPalette.emerald },
    { label: 'Idle', value: stats.idle, color: chartPalette.amber },
    { label: 'Offline', value: stats.offline, color: chartPalette.rose },
    { label: 'OpsPod', value: stats.opspod, color: chartPalette.blue },
    { label: 'Alt Mobility', value: stats.altmobility, color: chartPalette.cyan },
  ]

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        borderRadius: 2,
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.24)' : 'rgba(148, 163, 184, 0.22)'}`,
        color: theme.palette.mode === 'dark' ? '#e5edf8' : '#0f172a',
        background: theme.palette.mode === 'dark' ? 'rgba(17,24,39,0.78)' : 'rgba(255,255,255,0.86)',
        backdropFilter: 'blur(16px)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 24px 54px rgba(0, 0, 0, 0.22)'
            : '0 24px 54px rgba(15, 23, 42, 0.12)',
        overflow: 'hidden',
        position: 'relative',
        transformStyle: 'preserve-3d',
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(135deg, rgba(8,145,178,0.12), rgba(124,58,237,0.08) 42%, rgba(5,150,105,0.1))',
          pointerEvents: 'none',
        },
      })}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, position: 'relative', zIndex: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
          <Box sx={{ flex: '0 0 280px', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.4} sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: chartPalette.cyan,
                  backgroundColor: 'rgba(8,145,178,0.12)',
                  boxShadow: '0 12px 24px rgba(8,145,178,0.16)',
                }}
              >
                <SpeedIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={950}>
                  War Room Live Pulse
                </Typography>
                <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#64748b' }}>
                  Live IoT movement and provider split
                </Typography>
              </Box>
            </Stack>

            <Typography variant="h3" fontWeight={950} sx={{ color: chartPalette.blue, lineHeight: 1 }}>
              {formatNumber(stats.total)}
            </Typography>
            <Typography variant="caption" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#64748b', fontWeight: 800 }}>
              total live vehicles
            </Typography>

            <Grid container spacing={1} sx={{ mt: 1.5 }}>
              {statusItems.map((item) => (
                <Grid item xs={6} key={item.label}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      border: '1px solid rgba(148,163,184,0.18)',
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.58)' : 'rgba(255,255,255,0.58)',
                      boxShadow: `6px 8px 0 ${item.color}18`,
                      transform: 'skewX(-2deg)',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#64748b', fontWeight: 800 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={950} sx={{ color: item.color, lineHeight: 1.1 }}>
                      {formatNumber(item.value)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={950}>
                  Distance Leaders
                </Typography>
                <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#64748b' }}>
                  Highest running vehicle today and this month
                </Typography>
              </Box>
              <Chip label={loading ? 'Syncing' : 'Live'} size="small" color={loading ? 'warning' : 'success'} sx={{ fontWeight: 900 }} />
            </Stack>

            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', minWidth: 620, borderCollapse: 'separate', borderSpacing: '0 10px' }}>
                <Box component="thead">
                  <Box component="tr">
                    {['Window', 'Vehicle ID', 'Source', 'Distance'].map((header) => (
                      <Box component="th" key={header} sx={{ textAlign: 'left', px: 1.25, pb: 0.5, color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : '#64748b', fontSize: 12, fontWeight: 900 }}>
                        {header}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {leaders.map((leader) => (
                    <Box component="tr" key={leader.label}>
                      <Box component="td" sx={{ p: 1.25, borderRadius: '8px 0 0 8px', backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.68)' : 'rgba(255,255,255,0.72)', fontWeight: 900 }}>
                        {leader.label}
                      </Box>
                      <Box component="td" sx={{ p: 1.25, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.68)' : 'rgba(255,255,255,0.72)', fontWeight: 800 }}>
                        {formatVehicleLabel(leader.vehicleId)}
                      </Box>
                      <Box component="td" sx={{ p: 1.25, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.68)' : 'rgba(255,255,255,0.72)' }}>
                        <Chip label={leader.source} size="small" sx={{ fontWeight: 800 }} />
                      </Box>
                      <Box component="td" sx={{ p: 1.25, borderRadius: '0 8px 8px 0', backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.68)' : 'rgba(255,255,255,0.72)', color: chartPalette.emerald, fontWeight: 950 }}>
                        {leader.distanceKm.toFixed(2)} km
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

type IndiaMapCityInsight = {
  city: string
  state?: string
  vehicles: number
  riders: number
  activeRiders: number
  inactiveRiders: number
  readyToDeployVehicles: number
  deployedVehicles: number
  hubs: number
  clients: number
  evVehicles: number
  icVehicles: number
  vehicleOptions: string[]
}

const ALL_CITIES = 'All Cities'

type IndiaMapDisplayCity = IndiaMapCityInsight & {
  position: CityMapCenter
  pixelOffset: { x: number; y: number }
}

const riderBadgeLegend = [
  { label: '0-100 Riders', color: '#16a34a', min: 0, max: 100 },
  { label: '101-300 Riders', color: '#eab308', min: 101, max: 300 },
  { label: '301-500 Riders', color: '#f97316', min: 301, max: 500 },
  { label: '500+ Riders', color: '#dc2626', min: 501, max: Number.POSITIVE_INFINITY },
]

const getRiderBadgeColor = (riders: number) => (
  riderBadgeLegend.find((item) => riders >= item.min && riders <= item.max)?.color || riderBadgeLegend[0].color
)

const getMapBadgeSize = (count: number, active: boolean, large: boolean, cityCount: number) => {
  const digits = formatNumber(count).length
  const densityOffset = cityCount > 18 ? -8 : cityCount > 12 ? -5 : cityCount > 8 ? -2 : 0
  const base = (large ? 50 : 40) + densityOffset
  const digitBoost = digits > 4 ? 12 : digits > 3 ? 8 : digits > 2 ? 4 : 0
  return Math.max(32, Math.min(68, base + digitBoost + (active ? 8 : 0)))
}

const getMapBadgeFontSize = (value: number, badgeSize: number) => {
  const digits = formatNumber(value).length
  if (digits > 5) return Math.max(9, Math.floor(badgeSize * 0.22))
  if (digits > 4) return Math.max(10, Math.floor(badgeSize * 0.24))
  if (digits > 3) return Math.max(11, Math.floor(badgeSize * 0.27))
  return Math.max(12, Math.floor(badgeSize * 0.31))
}

const spreadNearbyCities = (
  cities: IndiaMapCityInsight[],
  selectedCity: string,
  centers: Record<string, CityMapCenter>,
): IndiaMapDisplayCity[] => {
  const positionedCities = cities
    .map((city) => {
      const position = centers[city.city]
      return position ? { ...city, position } : null
    })
    .filter((city): city is IndiaMapCityInsight & { position: CityMapCenter } => Boolean(city))

  if (selectedCity !== ALL_CITIES) {
    return positionedCities.map((city) => ({ ...city, pixelOffset: { x: 0, y: 0 } }))
  }

  const groups = new Map<string, Array<IndiaMapCityInsight & { position: CityMapCenter }>>()
  positionedCities.forEach((city) => {
    const key = `${Math.round(city.position.lat / 1.1)}:${Math.round(city.position.lng / 1.1)}`
    groups.set(key, [...(groups.get(key) || []), city])
  })

  return Array.from(groups.values()).flatMap((group) => {
    if (group.length === 1) return [{ ...group[0], pixelOffset: { x: 0, y: 0 } }]

    const radius = Math.min(86, 32 + group.length * 10)
    return group.map((city, index) => {
      const angle = (-Math.PI / 2) + (index * 2 * Math.PI) / group.length
      return {
        ...city,
        pixelOffset: {
          x: Math.round(Math.cos(angle) * radius),
          y: Math.round(Math.sin(angle) * radius),
        },
      }
    })
  })
}

function IndiaMapCard({
  cities,
  selectedCity,
  selectedVehicle,
  selectedRider,
  onCityChange,
  onVehicleChange,
  onRiderChange,
}: {
  cities: IndiaMapCityInsight[]
  selectedCity: string
  selectedVehicle: string
  selectedRider: string
  onCityChange: (city: string) => void
  onVehicleChange: (vehicle: string) => void
  onRiderChange: (rider: string) => void
}) {
  const [largeViewOpen, setLargeViewOpen] = useState(false)
  const [mapReadyTick, setMapReadyTick] = useState(0)
  const theme = useTheme()
  const isDarkMap = theme.palette.mode === 'dark'
  const mapRef = useRef<google.maps.Map | null>(null)
  const [dynamicCityCenters, setDynamicCityCenters] = useState<Record<string, CityMapCenter>>({})
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyDEGolYguCDq63H96no92SrIlBAsjZToGA',
  })
  const allCityCenters = useMemo(
    () => ({ ...cityMapCenters, ...dynamicCityCenters }),
    [dynamicCityCenters],
  )
  const currentCity = cities.find((city) => city.city === selectedCity) || cities[0] || {
    city: ALL_CITIES,
    vehicles: 0,
    riders: 0,
    activeRiders: 0,
    inactiveRiders: 0,
    readyToDeployVehicles: 0,
    deployedVehicles: 0,
    hubs: 0,
    clients: 0,
    evVehicles: 0,
    icVehicles: 0,
    vehicleOptions: ['All vehicles (0)'],
  }
  const mappedCities = cities.filter((city) => city.city !== ALL_CITIES && allCityCenters[city.city])
  const mapCenter = selectedCity !== ALL_CITIES && allCityCenters[selectedCity]
    ? allCityCenters[selectedCity]
    : indiaDefaultCenter
  const mapZoom = selectedCity !== ALL_CITIES && allCityCenters[selectedCity]?.zoom
    ? allCityCenters[selectedCity].zoom
    : 5
  const visibleCities = selectedCity === ALL_CITIES
    ? mappedCities
    : mappedCities.filter((city) => city.city === selectedCity)
  const displayCities = useMemo(
    () => spreadNearbyCities(visibleCities, selectedCity, allCityCenters),
    [allCityCenters, selectedCity, visibleCities],
  )
  const displayCitiesKey = displayCities.map((city) => `${city.city}:${city.vehicles}:${city.riders}:${city.clients}:${city.hubs}`).join('|')
  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    styles: isDarkMap ? darkOperationsMapStyles : lightOperationsMapStyles,
    restriction: {
      latLngBounds: {
        north: 36.5,
        south: 6,
        west: 66,
        east: 98,
      },
      strictBounds: false,
    },
  }), [isDarkMap])

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.Geocoder) return

    const missingCities = cities
      .map((city) => city.city)
      .filter((city) => city !== ALL_CITIES && !allCityCenters[city])

    if (!missingCities.length) return

    const geocoder = new window.google.maps.Geocoder()
    let cancelled = false

    missingCities.forEach((city) => {
      geocoder.geocode(
        {
          address: `${city}, India`,
          componentRestrictions: { country: 'IN' },
        },
        (results, status) => {
          if (cancelled || status !== 'OK' || !results?.[0]) return

          const location = results[0].geometry.location
          setDynamicCityCenters((prev) => (
            prev[city]
              ? prev
              : {
                  ...prev,
                  [city]: {
                    lat: location.lat(),
                    lng: location.lng(),
                    zoom: 11,
                  },
                }
          ))
        },
      )
    })

    return () => {
      cancelled = true
    }
  }, [allCityCenters, cities, isLoaded])

  useEffect(() => {
    if (!mapRef.current) return
    const map = mapRef.current

    window.setTimeout(() => {
      if (selectedCity === ALL_CITIES && displayCities.length > 1 && window.google?.maps) {
        const bounds = new window.google.maps.LatLngBounds()
        displayCities.forEach((city) => bounds.extend(city.position))
        map.fitBounds(bounds, {
          top: 64,
          right: 72,
          bottom: 64,
          left: 72,
        })
        return
      }

      map.panTo(mapCenter)
      map.setZoom(mapZoom)
    }, 80)
  }, [displayCitiesKey, mapCenter.lat, mapCenter.lng, mapZoom, selectedCity])

  const renderMapBody = (large = false) => (
    <>
      <Box
        sx={{
          height: large ? { xs: '58vh', md: '62vh' } : { xs: 300, md: 360 },
          borderRadius: 2,
          overflow: 'hidden',
          border: isDarkMap ? '1px solid rgba(56,189,248,0.24)' : '1px solid rgba(37,99,235,0.22)',
          boxShadow: large
            ? isDarkMap ? '0 24px 70px rgba(0,0,0,0.46)' : '0 24px 70px rgba(15,23,42,0.2)'
            : isDarkMap ? '0 18px 42px rgba(0,0,0,0.32)' : '0 18px 42px rgba(15,23,42,0.16)',
        }}
      >
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={(map) => {
              mapRef.current = map
              setMapReadyTick((tick) => tick + 1)
            }}
            onIdle={() => setMapReadyTick((tick) => tick + 1)}
            onUnmount={() => {
              mapRef.current = null
            }}
            options={mapOptions}
          >
            {displayCities.map((city) => {
              const active = city.city === selectedCity
              const vehicleSize = getMapBadgeSize(city.vehicles, active, large, displayCities.length)
              const riderSize = getMapBadgeSize(city.riders, active, large, displayCities.length)
              const maxSize = Math.max(vehicleSize, riderSize)
              const badges = [
                {
                  key: `${city.city}-vehicles`,
                  value: city.vehicles,
                  color: chartPalette.blue,
                  label: 'Vehicles',
                  size: vehicleSize,
                  tooltip: (
                    <Box sx={{ p: 0.5, color: isDarkMap ? '#0f172a' : '#f8fafc' }}>
                      <Typography variant="subtitle2" fontWeight={950} color="inherit">{city.city}</Typography>
                      <Typography variant="caption" display="block" color="inherit">RTD Vehicles: {formatNumber(city.readyToDeployVehicles)}</Typography>
                      <Typography variant="caption" display="block" color="inherit">Deployed Vehicles: {formatNumber(city.deployedVehicles)}</Typography>
                    </Box>
                  ),
                },
                {
                  key: `${city.city}-riders`,
                  value: city.riders,
                  color: getRiderBadgeColor(city.riders),
                  label: 'Riders',
                  size: riderSize,
                  tooltip: (
                    <Box sx={{ p: 0.5, color: isDarkMap ? '#0f172a' : '#f8fafc' }}>
                      <Typography variant="subtitle2" fontWeight={950} color="inherit">{city.city}</Typography>
                      <Typography variant="caption" display="block" color="inherit">Active Riders: {formatNumber(city.activeRiders)}</Typography>
                      <Typography variant="caption" display="block" color="inherit">Inactive Riders: {formatNumber(city.inactiveRiders)}</Typography>
                    </Box>
                  ),
                },
              ]

              return (
                <OverlayView
                  key={`${city.city}-${mapReadyTick}`}
                  position={city.position}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={() => ({
                    x: city.pixelOffset.x - ((vehicleSize + riderSize + (active ? 10 : 8)) / 2),
                    y: city.pixelOffset.y - (maxSize / 2),
                  })}
                >
                  <Stack
                    component={motion.div}
                    direction="row"
                    spacing={active ? 1.25 : 1}
                    initial={false}
                    animate={{ scale: active ? 1.05 : 1 }}
                    transition={{ type: 'spring', stiffness: 340, damping: 26 }}
                    sx={{
                      alignItems: 'center',
                      filter: active ? 'drop-shadow(0 14px 18px rgba(15,23,42,0.28))' : 'drop-shadow(0 10px 14px rgba(15,23,42,0.2))',
                      transformOrigin: 'center',
                    }}
                  >
                    {badges.map((badge) => (
                      <Tooltip
                        key={badge.key}
                        title={badge.tooltip}
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              borderRadius: 1.5,
                              backgroundColor: isDarkMap ? '#f8fafc' : '#0f172a',
                              color: isDarkMap ? '#0f172a' : '#f8fafc',
                              border: isDarkMap ? '1px solid rgba(148,163,184,0.24)' : '1px solid rgba(15,23,42,0.08)',
                              boxShadow: isDarkMap
                                ? '0 18px 36px rgba(0,0,0,0.45)'
                                : '0 18px 36px rgba(15,23,42,0.28)',
                            },
                          },
                          arrow: {
                            sx: {
                              color: isDarkMap ? '#f8fafc' : '#0f172a',
                            },
                          },
                        }}
                      >
                        <Box
                          component={motion.div}
                          role="button"
                          tabIndex={0}
                          aria-label={`${city.city} ${badge.label}: ${formatNumber(badge.value)}`}
                          whileHover={{ scale: 1.12 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onCityChange(city.city)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onCityChange(city.city)
                          }
                        }}
                        sx={{
                          width: badge.size,
                          height: badge.size,
                          borderRadius: '50%',
                          background: `conic-gradient(${badge.color} 0 58%, ${chartPalette.blue} 58% 78%, #f59e0b 78% 100%)`,
                          p: '5px',
                          boxShadow: active
                            ? '0 0 0 7px rgba(37,99,235,0.18), 0 16px 30px rgba(15,23,42,0.32)'
                            : '0 0 0 3px rgba(255,255,255,0.78), 0 10px 22px rgba(15,23,42,0.24)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          position: 'relative',
                          '&:before': {
                            content: '""',
                            position: 'absolute',
                            inset: active ? -8 : -4,
                            borderRadius: '50%',
                            border: active ? `2px solid ${chartPalette.blue}` : '1px solid rgba(255,255,255,0.8)',
                            pointerEvents: 'none',
                          },
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              backgroundColor: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#0f172a',
                              fontSize: getMapBadgeFontSize(badge.value, badge.size),
                              fontWeight: 950,
                              lineHeight: 1,
                            }}
                          >
                            {formatNumber(badge.value)}
                          </Box>
                        </Box>
                      </Tooltip>
                    ))}
                  </Stack>
                </OverlayView>
              )
            })}
          </GoogleMap>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>

      <Grid container spacing={1.25} sx={{ mt: 1.5 }}>
        {[
          { label: 'Vehicles', value: currentCity?.vehicles || 0, color: chartPalette.blue, icon: <ScooterIcon /> },
          { label: 'Riders', value: currentCity?.riders || 0, color: chartPalette.emerald, icon: <PeopleIcon /> },
          { label: 'Clients', value: currentCity?.clients || 0, color: chartPalette.amber, icon: <StoreIcon /> },
          { label: 'Hubs', value: currentCity?.hubs || 0, color: chartPalette.violet, icon: <HubIcon /> },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <Box
              component={motion.div}
              whileHover={{ y: -4, scale: 1.015 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
              sx={{
                p: { xs: 1.5, md: large ? 2.25 : 1.5 },
                minHeight: large ? 104 : 86,
                borderRadius: 2,
                border: '1px solid rgba(148,163,184,0.2)',
                background: `linear-gradient(135deg, rgba(255,255,255,0.94), rgba(255,255,255,0.72)), linear-gradient(135deg, ${item.color}24, transparent)`,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8), 0 16px 34px rgba(15,23,42,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: large ? 48 : 40,
                  height: large ? 48 : 40,
                  borderRadius: 1.5,
                  color: item.color,
                  backgroundColor: `${item.color}14`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 850 }}>
                  {item.label}
                </Typography>
                <Typography variant={large ? 'h4' : 'h6'} fontWeight={950} sx={{ color: item.color, lineHeight: 1.12 }}>
                  {formatNumber(item.value)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </>
  )

  return (
    <>
    <Card
      elevation={0}
      sx={(theme) => ({
        height: '100%',
        minHeight: { xs: 520, md: 590 },
        borderRadius: 2,
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(31,41,55,0.95)' : '#e2e8f0'}`,
        color: theme.palette.mode === 'dark' ? '#e5edf8' : '#0f172a',
        background: theme.palette.mode === 'dark' ? 'rgba(17,24,39,0.78)' : 'rgba(255,255,255,0.84)',
        backdropFilter: 'blur(18px)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 34px 74px rgba(0, 0, 0, 0.36)'
            : '0 34px 74px rgba(37, 99, 235, 0.13), 0 18px 44px rgba(15, 23, 42, 0.10)',
        overflow: 'hidden',
        position: 'relative',
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: isDarkMap
            ? 'linear-gradient(135deg, rgba(14,165,233,0.18), rgba(16,185,129,0.12), rgba(245,158,11,0.08))'
            : 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(5,150,105,0.12), rgba(245,158,11,0.08))',
          pointerEvents: 'none',
        },
        '&:after': {
          content: '""',
          position: 'absolute',
          inset: 8,
          borderRadius: 1.5,
          border: '1px solid rgba(255,255,255,0.65)',
          pointerEvents: 'none',
        },
      })}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, position: 'relative', zIndex: 1, height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="h5" fontWeight={950}>
              India Operations Map
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMap ? '#9ca3af' : '#64748b', mt: 0.5 }}>
              City-wise riders and vehicle spread
            </Typography>
          </Box>
          <Stack spacing={1} alignItems="flex-end">
            <Chip
              label="LIVE TRACKING"
              size="small"
              sx={{
                color: isDarkMap ? '#bfdbfe' : '#1d4ed8',
                backgroundColor: isDarkMap ? 'rgba(59,130,246,0.16)' : '#dbeafe',
                border: isDarkMap ? '1px solid rgba(59,130,246,0.28)' : '1px solid rgba(37,99,235,0.18)',
                fontWeight: 950,
              }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInFullIcon fontSize="small" />}
              onClick={() => setLargeViewOpen(true)}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 900,
                boxShadow: '0 12px 24px rgba(37,99,235,0.28)',
              }}
            >
              View Large
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={7} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>City</InputLabel>
              <Select label="City" value={selectedCity} onChange={(event) => onCityChange(String(event.target.value))}>
                {cities.map((city) => (
                  <MenuItem key={city.city} value={city.city}>{city.city}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5} md={7}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
              {[
                // { label: 'Online Riders', value: formatNumber(currentCity.activeRiders || 0) },
                // { label: 'Last Updated', value: 'Just Now' },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    px: 1.2,
                    py: 0.75,
                    borderRadius: 1.5,
                    border: isDarkMap ? '1px solid rgba(148,163,184,0.18)' : '1px solid rgba(226,232,240,0.95)',
                    background: isDarkMap ? 'rgba(15,23,42,0.58)' : 'rgba(255,255,255,0.72)',
                    minWidth: 112,
                  }}
                >
                  <Typography variant="caption" sx={{ color: isDarkMap ? '#9ca3af' : '#64748b', fontWeight: 800, display: 'block', lineHeight: 1 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDarkMap ? '#e5edf8' : '#0f172a', fontWeight: 950, display: 'block', mt: 0.35 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>

        {renderMapBody()}

      </CardContent>
    </Card>
    <Dialog
      open={largeViewOpen}
      onClose={() => setLargeViewOpen(false)}
      fullScreen
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          color: isDarkMap ? '#e5edf8' : '#0f172a',
          background: isDarkMap
            ? 'linear-gradient(135deg, #061426, #082f2f 58%, #1f1305)'
            : 'linear-gradient(135deg, #eaf3ff, #ecfdf5 58%, #fff7ed)',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 1.5, md: 3 }, overflow: 'auto' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={950}>
              India Operations Command Center
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMap ? '#9fb3c8' : '#64748b', mt: 0.5 }}>
              Full-screen city-wise rider, vehicle, and hub visibility
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-end' }}>
            <Chip label="Interactive" size="small" sx={{ color: '#065f46', backgroundColor: '#dcfce7', fontWeight: 900 }} />
            <IconButton onClick={() => setLargeViewOpen(false)} aria-label="Close map">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={5} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>City</InputLabel>
              <Select label="City" value={selectedCity} onChange={(event) => onCityChange(String(event.target.value))}>
                {cities.map((city) => (
                  <MenuItem key={city.city} value={city.city}>{city.city}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {renderMapBody(true)}
      </DialogContent>
    </Dialog>
    </>
  )
}

export default function ManagementDashboard() {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const modeAccent = isDark ? '#16a34a' : chartPalette.blue
  const modeAccentSoft = isDark ? '#dcfce7' : '#dbeafe'
  const panelText = isDark ? '#e5edf8' : '#0f172a'
  const mutedText = isDark ? '#9ca3af' : '#64748b'
  const panelBorder = isDark ? 'rgba(31,41,55,0.92)' : '#e2e8f0'
  const panelSurface = isDark ? 'rgba(17,24,39,0.78)' : 'rgba(255,255,255,0.82)'
  const { user } = useAuth()
  const [metrics, setMetrics] = useState<ManagementDashboardMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [topCities, setTopCities] = useState<Array<{ city: string; totalOrders: number }>>([])
  const [evCityData, setEvCityData] = useState<Array<{ city: string; count: number }>>([])
  const [icCityData, setIcCityData] = useState<Array<{ city: string; count: number }>>([])
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [hoveredVehicleType, setHoveredVehicleType] = useState<string | null>(null)
  const [dashboardCities, setDashboardCities] = useState<Array<{ name: string; state?: string }>>([])
  const [cityRiderCounts, setCityRiderCounts] = useState<Record<string, number>>({})
  const [cityActiveRiderCounts, setCityActiveRiderCounts] = useState<Record<string, number>>({})
  const [cityInactiveRiderCounts, setCityInactiveRiderCounts] = useState<Record<string, number>>({})
  const [cityVehicleCounts, setCityVehicleCounts] = useState<Record<string, number>>({})
  const [cityReadyToDeployVehicleCounts, setCityReadyToDeployVehicleCounts] = useState<Record<string, number>>({})
  const [cityDeployedVehicleCounts, setCityDeployedVehicleCounts] = useState<Record<string, number>>({})
  const [cityHubCounts, setCityHubCounts] = useState<Record<string, number>>({})
  const [cityClientCounts, setCityClientCounts] = useState<Record<string, number>>({})
  const [selectedOperationsCity, setSelectedOperationsCity] = useState('')
  const [selectedOperationsVehicle, setSelectedOperationsVehicle] = useState('')
  const [selectedOperationsRider, setSelectedOperationsRider] = useState('')
  const [warRoomLeaders, setWarRoomLeaders] = useState<WarRoomDistanceLeader[]>([])
  const [warRoomLoading, setWarRoomLoading] = useState(false)
  const warRoomVehicles = useWarRoomSocket()

  const API_URL = import.meta.env.VITE_API_GATEWAY_URL || 'http://localhost:8000/api'
  const VITE_API_URL = `${API_URL}/api`
  const WAR_ROOM_API_BASE = normalizeApiBase(import.meta.env.VITE_API_GATEWAY_URL)

  const uniqueWarRoomVehicles = useMemo(() => {
    const seen = new Map<string, any>()
    warRoomVehicles.forEach((vehicle: any) => {
      const id = String(vehicle.vehicleId || vehicle.uniqueId || '')
      if (!id) return
      seen.set(id, vehicle)
    })
    return Array.from(seen.values())
  }, [warRoomVehicles])

  const warRoomStats = useMemo(() => {
    return uniqueWarRoomVehicles.reduce(
      (acc, vehicle: any) => {
        const id = String(vehicle.vehicleId || vehicle.uniqueId || '')
        const speed = Number(vehicle.speed || 0)
        const ignition = Boolean(vehicle.ignition)

        acc.total += 1
        if (id.startsWith('opspod:')) acc.opspod += 1
        if (id.startsWith('alt:')) acc.altmobility += 1

        if (speed > 0) acc.moving += 1
        else if (ignition) acc.idle += 1
        else acc.offline += 1

        return acc
      },
      { total: 0, opspod: 0, altmobility: 0, moving: 0, idle: 0, offline: 0 },
    )
  }, [uniqueWarRoomVehicles])

  const loadWarRoomLeaders = useCallback(async () => {
    setWarRoomLoading(true)
    try {
      const today = getTodayKey()
      const [dailyRes, monthlyRes] = await Promise.all([
        fetch(`${WAR_ROOM_API_BASE}/api/war-room/distance-analytics?date=${today}`),
        fetch(`${WAR_ROOM_API_BASE}/api/war-room/distance-analytics/monthly?date=${today}`),
      ])

      const [dailyJson, monthlyJson] = await Promise.all([
        dailyRes.ok ? dailyRes.json() : Promise.resolve({ vehicles: [] }),
        monthlyRes.ok ? monthlyRes.json() : Promise.resolve({ vehicles: [] }),
      ])

      const getLeader = (label: string, vehicles: any[]): WarRoomDistanceLeader => {
        const top = [...(vehicles || [])].sort((a, b) => Number(b.distance_km || 0) - Number(a.distance_km || 0))[0]
        const vehicleId = String(top?.vehicle_id || '')
        const source = vehicleId.startsWith('opspod:') ? 'OpsPod' : vehicleId.startsWith('alt:') ? 'Alt Mobility' : 'War Room'

        return {
          label,
          vehicleId: vehicleId || 'NA',
          distanceKm: Number(top?.distance_km || 0),
          source,
        }
      }

      setWarRoomLeaders([
        getLeader('Today Highest', dailyJson?.vehicles || []),
        getLeader('Month Highest', monthlyJson?.vehicles || []),
      ])
    } catch (err) {
      console.error('Error loading War Room leaders:', err)
      setWarRoomLeaders([
        { label: 'Today Highest', vehicleId: 'NA', distanceKm: 0, source: 'War Room' },
        { label: 'Month Highest', vehicleId: 'NA', distanceKm: 0, source: 'War Room' },
      ])
    } finally {
      setWarRoomLoading(false)
    }
  }, [WAR_ROOM_API_BASE])

  const indiaMapCities = useMemo<IndiaMapCityInsight[]>(() => {
    const cityMap = new Map<string, IndiaMapCityInsight>()

    const ensureCity = (cityName: string, state?: string) => {
      const city = normalizeIndiaCityName(cityName)
      if (!city) return null
      if (!cityMap.has(city)) {
        cityMap.set(city, {
          city,
          state,
          vehicles: 0,
          riders: cityRiderCounts[city] || 0,
          activeRiders: cityActiveRiderCounts[city] || 0,
          inactiveRiders: cityInactiveRiderCounts[city] || 0,
          readyToDeployVehicles: cityReadyToDeployVehicleCounts[city] || 0,
          deployedVehicles: cityDeployedVehicleCounts[city] || 0,
          hubs: cityHubCounts[city] || 0,
          clients: cityClientCounts[city] || 0,
          evVehicles: 0,
          icVehicles: 0,
          vehicleOptions: [],
        })
      }
      const existing = cityMap.get(city)!
      if (state && !existing.state) existing.state = state
      existing.riders = cityRiderCounts[city] || existing.riders || 0
      existing.activeRiders = cityActiveRiderCounts[city] || existing.activeRiders || 0
      existing.inactiveRiders = cityInactiveRiderCounts[city] || existing.inactiveRiders || 0
      existing.vehicles = Object.prototype.hasOwnProperty.call(cityVehicleCounts, city)
        ? cityVehicleCounts[city]
        : existing.vehicles || 0
      existing.readyToDeployVehicles = cityReadyToDeployVehicleCounts[city] || existing.readyToDeployVehicles || 0
      existing.deployedVehicles = cityDeployedVehicleCounts[city] || existing.deployedVehicles || 0
      existing.hubs = cityHubCounts[city] || existing.hubs || 0
      existing.clients = cityClientCounts[city] || existing.clients || 0
      return existing
    }

    dashboardCities.forEach((city) => ensureCity(city.name, city.state))
    Object.entries(cityVehicleCounts).forEach(([cityName, count]) => {
      const city = ensureCity(cityName)
      if (city) city.vehicles = count
    })
    Object.entries(cityHubCounts).forEach(([cityName, count]) => {
      const city = ensureCity(cityName)
      if (city) city.hubs = count
    })
    evCityData.forEach((item) => {
      const city = ensureCity(item.city)
      if (city) city.evVehicles = item.count
    })
    icCityData.forEach((item) => {
      const city = ensureCity(item.city)
      if (city) city.icVehicles = item.count
    })

    ;['Bengaluru', 'Hyderabad', 'Chennai', 'Delhi', 'Noida', 'Gurugram', 'Mumbai', 'Mysuru', 'Coimbatore'].forEach((city) => ensureCity(city))

    const cityRows = Array.from(cityMap.values())
      .map((city) => {
        const readyToDeployVehicles = cityReadyToDeployVehicleCounts[city.city] ?? city.readyToDeployVehicles
        const deployedVehicles = cityDeployedVehicleCounts[city.city] ?? city.deployedVehicles
        const vehicles = Object.prototype.hasOwnProperty.call(cityVehicleCounts, city.city)
          ? cityVehicleCounts[city.city]
          : 0
        return {
          ...city,
          vehicles,
          readyToDeployVehicles,
          deployedVehicles,
          riders: city.riders || city.activeRiders + city.inactiveRiders,
          vehicleOptions: [`All vehicles (${formatNumber(vehicles)})`],
        }
      })
      .sort((a, b) => (b.vehicles + b.riders) - (a.vehicles + a.riders))

    const cityTotals = cityRows.reduce(
      (acc, city) => {
        acc.vehicles += city.vehicles
        acc.riders += city.riders
        acc.activeRiders += city.activeRiders
        acc.inactiveRiders += city.inactiveRiders
        acc.readyToDeployVehicles += city.readyToDeployVehicles
        acc.deployedVehicles += city.deployedVehicles
        acc.hubs += city.hubs
        acc.clients += city.clients
        acc.evVehicles += city.evVehicles
        acc.icVehicles += city.icVehicles
        return acc
      },
      { vehicles: 0, riders: 0, activeRiders: 0, inactiveRiders: 0, readyToDeployVehicles: 0, deployedVehicles: 0, hubs: 0, clients: 0, evVehicles: 0, icVehicles: 0 },
    )
    const totals = {
      ...cityTotals,
      vehicles: metrics?.totalFleetSize || cityTotals.vehicles,
      riders: metrics?.totalRiders || cityTotals.riders,
      activeRiders: metrics?.activeRiders || cityTotals.activeRiders,
      inactiveRiders: metrics?.inactiveRiders || cityTotals.inactiveRiders,
      readyToDeployVehicles: metrics?.availableVehicles || cityTotals.readyToDeployVehicles,
      deployedVehicles: metrics?.assignedVehicles || cityTotals.deployedVehicles,
      clients: metrics?.totalClients || cityTotals.clients,
    }

    return [
      {
        city: ALL_CITIES,
        vehicles: totals.vehicles,
        riders: totals.riders,
        activeRiders: totals.activeRiders,
        inactiveRiders: totals.inactiveRiders,
        readyToDeployVehicles: totals.readyToDeployVehicles,
        deployedVehicles: totals.deployedVehicles,
        hubs: cityTotals.hubs,
        clients: totals.clients,
        evVehicles: totals.evVehicles,
        icVehicles: totals.icVehicles,
        vehicleOptions: [
          `All vehicles (${formatNumber(totals.vehicles)})`,
        ],
      },
      ...cityRows,
    ]
  }, [cityActiveRiderCounts, cityClientCounts, cityDeployedVehicleCounts, cityHubCounts, cityInactiveRiderCounts, cityReadyToDeployVehicleCounts, cityRiderCounts, cityVehicleCounts, dashboardCities, evCityData, icCityData, metrics?.activeRiders, metrics?.assignedVehicles, metrics?.availableVehicles, metrics?.inactiveRiders, metrics?.totalClients, metrics?.totalFleetSize, metrics?.totalRiders])

  const handleOperationsCityChange = useCallback((city: string) => {
    setSelectedOperationsCity(city)
    const target = indiaMapCities.find((item) => item.city === city)
    setSelectedOperationsVehicle(`All vehicles (${formatNumber(target?.vehicles || 0)})`)
    setSelectedOperationsRider(`All riders (${formatNumber(target?.riders || 0)})`)
  }, [indiaMapCities])

  const loadDashboardData = useCallback(async () => {
    try {
      setError(null)
      setLoading(true)

      const [
        dashboardRes,
        clientsRes,
        clientTotalsRes,
        riderStatsRes,
        vehicleStatsRes,
        hubsRes,
        ordersRes,
        citySummaryRes,
        evVehicleRes,
        icVehicleRes,
        evCityRes,
        icCityRes,
        citiesRes,
      ] = await Promise.all([
        dashboardApi.getManagementDashboard({ department: DepartmentType.MANAGEMENT }),
        clientStoreService.getClients({ page: 1, limit: 1 }),
        clientStoreService.getClientTotalsByName().catch((err) => {
          console.error('Error fetching total client counts:', err)
          return { success: false, totalClients: 0 }
        }),
        riderService.getRiderStats(),
        vehicleService.getVehicleAnalytics('custom', undefined),
        hubService.getHubs(),
        ordersService.getOrders(),
        ordersService.getCitySummary({}),
        fetch(`${VITE_API_URL}/v1/additional-apis/ev-vehicle-count`, { method: 'GET' })
          .then(async (res) => {
            if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`)
            return await res.json()
          })
          .catch((err) => {
            console.error('Error fetching EV vehicle count:', err)
            return { data: { count: 0 } }
          }),
        fetch(`${VITE_API_URL}/v1/additional-apis/ic-vehicle-count`, { method: 'GET' })
          .then(async (res) => {
            if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`)
            return await res.json()
          })
          .catch((err) => {
            console.error('Error fetching IC vehicle count:', err)
            return { data: { count: 0 } }
          }),
        fetch(`${VITE_API_URL}/v1/additional-apis/ev-vehicle-count-by-city`, { method: 'GET' })
          .then(async (res) => {
            if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`)
            return await res.json()
          })
          .catch((err) => {
            console.error('Error fetching EV vehicle by city:', err)
            return { data: [] }
          }),
        fetch(`${VITE_API_URL}/v1/additional-apis/ic-vehicle-count-by-city`, { method: 'GET' })
          .then(async (res) => {
            if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`)
            return await res.json()
          })
          .catch((err) => {
            console.error('Error fetching IC vehicle by city:', err)
            return { data: [] }
          }),
        cityService.getCities().catch((err) => {
          console.error('Error fetching dashboard cities:', err)
          return []
        }),
      ])

      const riderStats = (riderStatsRes?.data || {}) as any
      const vehicleSummary = (vehicleStatsRes?.data?.summary || {}) as any
      const hubsPayload = hubsRes as any
      const hubs = Array.isArray(hubsPayload) ? hubsPayload : (hubsPayload?.data || [])
      const orders = ordersRes?.data || []
      const citySummary = citySummaryRes?.summary || []
      const evVehicleCount = evVehicleRes?.data?.count || 0
      const icVehicleCount = icVehicleRes?.data?.count || 0

      const ordersTotalCount = ordersRes?.pagination?.total ?? orders.length
      const analyticsTotalOrders = (citySummary || []).reduce((sum: number, c: any) => sum + (c.totalOrders || 0), 0)
      const totalDeliveries = analyticsTotalOrders || ordersTotalCount || orders.reduce((sum, order) => sum + (order.totalOrdersDay || 0), 0)

      const evCityArray = (evCityRes?.data || []).map((item: any) => ({
        city: item.city,
        count: item._count?._all || 0,
      }))
      const icCityArray = (icCityRes?.data || []).map((item: any) => ({
        city: item.city,
        count: item._count?._all || 0,
      }))

      setEvCityData(evCityArray)
      setIcCityData(icCityArray)
      setDashboardCities((citiesRes || []).map((city: any) => ({
        name: normalizeIndiaCityName(city.name || city.cityName || city.city),
        state: city.state,
      })).filter((city: any) => city.name))

      const hubCounts: Record<string, number> = {}
      hubs.forEach((hub: any) => {
        const city = normalizeIndiaCityName(hub.cityName || hub.city?.name || hub.city || hub.location)
        if (!city) return
        hubCounts[city] = (hubCounts[city] || 0) + 1
      })
      setCityHubCounts(hubCounts)

      const statusByCity = vehicleStatsRes?.data?.statusByCity
      if (statusByCity?.cities?.length && statusByCity?.totalPerCity?.length) {
        setCityVehicleCounts(Object.fromEntries(
          statusByCity.cities.map((city: string, index: number) => [
            normalizeIndiaCityName(city),
            Number(statusByCity.totalPerCity[index] || 0),
          ]),
        ))

        const findStatusRow = (statusLabels: string[]) => (
          statusByCity.rows?.find((row: any) => statusLabels.includes(String(row.status || '').toLowerCase()))
        )
        const rtdRow = findStatusRow(['available', 'ready to deploy', 'ready_to_deploy'])
        const deployedRow = findStatusRow(['assigned', 'deployed', 'deployed vehicles'])

        setCityReadyToDeployVehicleCounts(Object.fromEntries(
          statusByCity.cities.map((city: string, index: number) => [
            normalizeIndiaCityName(city),
            Number(rtdRow?.counts?.[index] || 0),
          ]),
        ))
        setCityDeployedVehicleCounts(Object.fromEntries(
          statusByCity.cities.map((city: string, index: number) => [
            normalizeIndiaCityName(city),
            Number(deployedRow?.counts?.[index] || 0),
          ]),
        ))
      }

      const top3Cities = citySummary
        .sort((a: any, b: any) => (b.totalOrders || 0) - (a.totalOrders || 0))
        .slice(0, 3)

      setTopCities(top3Cities)
      console.log('Dashboard API Response:', dashboardRes)

      const mergedData = {
        ...dashboardRes,
        totalRiders: riderStats.totalRiders ?? 0,
        activeRiders: riderStats.activeRiders ?? 0,
        inactiveRiders: (riderStats.totalRiders ?? 0) - (riderStats.activeRiders ?? 0),
        totalClients: clientTotalsRes?.totalClients ?? clientsRes?.pagination?.totalItems ?? 0,
        totalFleetSize: vehicleSummary.totalVehicles ?? 0,
        availableVehicles: vehicleSummary.activeVehicles ?? 0,
        assignedVehicles: vehicleSummary.assignedVehicles ?? 0,
        underMaintenance: vehicleSummary.underMaintenance ?? 0,
        evVehicleCount,
        icVehicleCount,
        totalHubs: hubs.length ?? 0,
        totalDeliveries: totalDeliveries || ((dashboardRes as any)?.totalDeliveries ?? 0),
        avgDeliveryTime: (dashboardRes as any)?.avgDeliveryTime ?? 0,
        onTimeRate: (dashboardRes as any)?.onTimeRate ?? 0,
      }

      setMetrics(mergedData)
      setLastUpdated(new Date())
    } catch (err: any) {
      console.error('Error loading management dashboard:', err)
      setError(err.message || 'Failed to load management dashboard')
    } finally {
      setLoading(false)
    }
  }, [VITE_API_URL])

  useEffect(() => {
    loadDashboardData()

    const handleRiderStatsUpdate = () => loadDashboardData()
    const handleVehicleStatsUpdate = () => loadDashboardData()

    window.addEventListener('riderStatsUpdated', handleRiderStatsUpdate)
    window.addEventListener('vehicleStatsUpdated', handleVehicleStatsUpdate)

    return () => {
      window.removeEventListener('riderStatsUpdated', handleRiderStatsUpdate)
      window.removeEventListener('vehicleStatsUpdated', handleVehicleStatsUpdate)
    }
  }, [loadDashboardData])

  useEffect(() => {
    if (!selectedOperationsCity && indiaMapCities.length) {
      setSelectedOperationsCity(ALL_CITIES)
    }
  }, [indiaMapCities, selectedOperationsCity])

  useEffect(() => {
    const current = indiaMapCities.find((city) => city.city === (selectedOperationsCity || ALL_CITIES))
    if (!current) return
    setSelectedOperationsVehicle(`All vehicles (${formatNumber(current.vehicles)})`)
    setSelectedOperationsRider(`All riders (${formatNumber(current.riders)})`)
  }, [indiaMapCities, selectedOperationsCity])

  useEffect(() => {
    const citiesToLoad = indiaMapCities.filter((city) => city.city !== ALL_CITIES).map((city) => city.city)
    if (!citiesToLoad.length) return

    let cancelled = false

    const loadCityRiderStatusCounts = async () => {
      const response = await fetch(`${VITE_API_URL}/v1/additional-apis/rider-status-count-by-city`, { method: 'GET' })
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`)
      const payload = await response.json()
      const cityStats = new Map<string, { total: number; active: number; inactive: number }>()

      ;(payload?.data || []).forEach((item: any) => {
        const city = normalizeIndiaCityName(item?.city)
        if (!city) return
        cityStats.set(city, {
          total: Number(item?.total || 0),
          active: Number(item?.active || 0),
          inactive: Number(item?.inactive || 0),
        })
      })

      return citiesToLoad.map((city) => {
        const stats = cityStats.get(city) || { total: 0, active: 0, inactive: 0 }
        return [city, stats.total, stats.active, stats.inactive] as const
      })
    }

    loadCityRiderStatusCounts().then((entries) => {
      if (cancelled) return
      setCityRiderCounts((prev) => ({
        ...prev,
        ...Object.fromEntries(entries.map(([city, total]) => [city, total])),
      }))
      setCityActiveRiderCounts((prev) => ({
        ...prev,
        ...Object.fromEntries(entries.map(([city, _total, active]) => [city, active])),
      }))
      setCityInactiveRiderCounts((prev) => ({
        ...prev,
        ...Object.fromEntries(entries.map(([city, _total, _active, inactive]) => [city, inactive])),
      }))
    }).catch((err) => {
      console.error('Error fetching rider counts for operations map:', err)
    })

    return () => {
      cancelled = true
    }
  }, [VITE_API_URL, indiaMapCities.map((city) => city.city).join('|')])

  useEffect(() => {
    const citiesToLoad = indiaMapCities.filter((city) => city.city !== ALL_CITIES).map((city) => city.city)
    if (!citiesToLoad.length) return

    let cancelled = false

    clientStoreService.getClientCountByCity().then((res) => {
      if (cancelled) return
      const countsByCity = (res.data || []).reduce((acc: Record<string, number>, item: any) => {
        const city = normalizeIndiaCityName(item?.city)
        if (!city) return acc
        acc[city] = (acc[city] || 0) + Number(item?.count || 0)
        return acc
      }, {})

      setCityClientCounts((prev) => ({
        ...prev,
        ...Object.fromEntries(citiesToLoad.map((city) => [city, countsByCity[city] || 0])),
      }))
    }).catch((err) => {
      console.error('Error fetching client counts for operations map:', err)
    })

    return () => {
      cancelled = true
    }
  }, [indiaMapCities.map((city) => city.city).join('|')])

  useEffect(() => {
    loadWarRoomLeaders()
  }, [loadWarRoomLeaders])

  const dashboardCharts = useMemo(() => {
    if (!metrics) return null

    const totalEnergyVehicles = metrics.evVehicleCount + metrics.icVehicleCount
    const cityOrders = topCities.length
      ? topCities.map((city) => ({
          city: city.city,
          orders: city.totalOrders || 0,
        }))
      : [
          { city: 'No city data', orders: 0 },
        ]

    return {
     commandTrend: [
  {
    name: 'Riders',
    active: metrics.activeRiders,
    total: metrics.totalRiders,
    actualActive: metrics.activeRiders,
    actualTotal: metrics.totalRiders,
  },
  {
    name: 'Fleet',
    active: metrics.assignedVehicles,
    total: metrics.totalFleetSize,
    actualActive: metrics.assignedVehicles,
    actualTotal: metrics.totalFleetSize,
  },
  {
    name: 'Orders',
    active: metrics.totalDeliveries,
    total: Math.max(metrics.totalDeliveries, metrics.activeRiders),
    actualActive: metrics.totalDeliveries,
    actualTotal: Math.max(metrics.totalDeliveries, metrics.activeRiders),
  },
  {
    name: 'Clients',
    active: metrics.totalClients,
    total: Math.max(metrics.totalClients, metrics.totalStores || 0),
    actualActive: metrics.totalClients,
    actualTotal: Math.max(metrics.totalClients, metrics.totalStores || 0),
  },
],
      departmentBars: [
        { name: 'Orders', value: metrics.totalDeliveries, fill: modeAccent },
        { name: 'Riders', value: metrics.activeRiders, fill: chartPalette.emerald },
        { name: 'Fleet', value: metrics.totalFleetSize, fill: chartPalette.cyan },
        { name: 'Clients', value: metrics.totalClients, fill: chartPalette.amber },
        { name: 'Hubs', value: metrics.totalHubs, fill: chartPalette.violet },
      ],
      riderSplit: [
        { name: 'Active', value: metrics.activeRiders, fill: chartPalette.emerald },
        { name: 'Inactive', value: metrics.inactiveRiders, fill: chartPalette.rose },
      ],
      fleetStatus: [
        { name: 'Deployed', value: metrics.assignedVehicles, fill: chartPalette.emerald },
        { name: 'RTD', value: metrics.availableVehicles, fill: modeAccent },
        { name: 'Maintenance', value: metrics.underMaintenance, fill: chartPalette.amber },
      ],
      energyMix: [
        { name: 'EV', value: metrics.evVehicleCount, fill: chartPalette.emerald },
        { name: 'IC', value: metrics.icVehicleCount, fill: chartPalette.cyan },
      ],
      cityOrders,
      clientMomentum: [
        { name: 'Total Clients', value: metrics.totalClients, fill: modeAccent },
        { name: 'Stores', value: metrics.totalStores || 0, fill: chartPalette.cyan },
        { name: 'New Month', value: metrics.newClientsThisMonth || 0, fill: chartPalette.emerald },
      ],
      healthGauges: [
        {
          label: 'Active Riders',
          value: metrics.totalRiders ? (metrics.activeRiders / metrics.totalRiders) * 100 : 0,
          color: chartPalette.emerald,
        },
        {
          label: 'Fleet Deployed',
          value: metrics.totalFleetSize ? (metrics.assignedVehicles / metrics.totalFleetSize) * 100 : 0,
          color: modeAccent,
        },
        {
          label: 'EV Mix',
          value: totalEnergyVehicles ? (metrics.evVehicleCount / totalEnergyVehicles) * 100 : 0,
          color: chartPalette.violet,
        },
        {
          label: 'On-Time',
          value: metrics.onTimeRate || 0,
          color: chartPalette.amber,
        },
      ],
    }
  }, [metrics, topCities, modeAccent])

  function CustomXAxisTick({ x, y, payload }: any) {
    if (!dashboardCharts || !dashboardCharts.commandTrend) return null

    const item = dashboardCharts.commandTrend.find((d: any) => d.name === payload.value)
    const totalLabel = item ? formatNumber(item.actualTotal) : ''
    const activeLabel = item ? formatNumber(item.actualActive) : ''

    return (
      <g transform={`translate(${x},${y + 6})`}>
        <text x={0} y={0} textAnchor="middle" fill="#334155" fontSize={12} fontWeight={700}>
          {payload.value}
        </text>
        <text x={0} y={16} textAnchor="middle" fill="#059669" fontSize={11}>
          {totalLabel}
        </text>
        <text x={0} y={32} textAnchor="middle" fill="#2563eb" fontSize={11}>
          {activeLabel}
        </text>
      </g>
    )
  }

  function CustomTooltip({ active, payload, label }: any) {
    if (!active || !payload || !payload.length) return null
    const data = payload[0].payload

    return (
      <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,0.18)', padding: 12, borderRadius: 6 }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>{label}</div>
        <div style={{ color: chartPalette.emerald, fontWeight: 800 }}>total : {formatNumber(data.actualTotal)}</div>
        <div style={{ color: modeAccent, marginTop: 4 }}>active : {formatNumber(data.actualActive)}</div>
      </div>
    )
  }

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" minHeight="80vh" justifyContent="center" gap={2}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Loading dashboard...</Typography>
      </Box>
    )
  }

  if (error || !metrics) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'No data available'}</Alert>
      </Box>
    )
  }

  const fleetDeploymentRate = metrics.totalFleetSize ? (metrics.assignedVehicles / metrics.totalFleetSize) * 100 : 0
  const evMixRate = (metrics.evVehicleCount + metrics.icVehicleCount)
    ? (metrics.evVehicleCount / (metrics.evVehicleCount + metrics.icVehicleCount)) * 100
    : 0
  const topMetricCards = [
    {
      label: 'Total Riders',
      value: formatNumber(metrics.totalRiders),
      helper: `${formatPercentage(metrics.totalRiders ? (metrics.activeRiders / metrics.totalRiders) * 100 : 0)} active`,
      icon: <PeopleIcon />,
      color: chartPalette.cyan,
      soft: '#cffafe',
      bars: [metrics.inactiveRiders, metrics.activeRiders, metrics.totalRiders],
    },
    {
      label: 'Active Riders',
      value: formatNumber(metrics.activeRiders),
      helper: 'Available rider force',
      icon: <PeopleIcon />,
      color: chartPalette.emerald,
      soft: '#d1fae5',
      bars: [metrics.activeRiders * 0.55, metrics.activeRiders * 0.78, metrics.activeRiders],
    },
    {
      label: 'Inactive Riders',
      value: formatNumber(metrics.inactiveRiders),
      helper: 'Needs attention',
      icon: <PeopleIcon />,
      color: chartPalette.rose,
      soft: '#ffe4e6',
      bars: [metrics.inactiveRiders * 0.72, metrics.inactiveRiders, metrics.inactiveRiders * 0.48],
    },
    {
      label: 'Total Clients',
      value: formatNumber(metrics.totalClients),
      helper: `${formatNumber(metrics.totalStores || 0)} stores mapped`,
      icon: <StoreIcon />,
      color: chartPalette.amber,
      soft: '#fef3c7',
      bars: [metrics.newClientsThisMonth || 0, metrics.totalStores || 0, metrics.totalClients],
    },
    {
      label: 'EV Fleet Size',
      value: formatNumber(metrics.totalFleetSize),
      helper: `${formatPercentage(fleetDeploymentRate)} deployed`,
      icon: <ScooterIcon />,
      color: modeAccent,
      soft: modeAccentSoft,
      bars: [metrics.availableVehicles, metrics.assignedVehicles, metrics.totalFleetSize],
    },
    {
      label: 'Deployed Vehicles',
      value: formatNumber(metrics.assignedVehicles),
      helper: 'Assigned to riders',
      icon: <ScooterIcon />,
      color: chartPalette.emerald,
      soft: '#d1fae5',
      bars: [metrics.assignedVehicles * 0.42, metrics.assignedVehicles * 0.8, metrics.assignedVehicles],
    },
    {
      label: 'RTD Vehicles',
      value: formatNumber(metrics.availableVehicles),
      helper: 'Ready to deploy',
      icon: <ScooterIcon />,
      color: chartPalette.violet,
      soft: '#ede9fe',
      bars: [metrics.availableVehicles * 0.52, metrics.availableVehicles, metrics.availableVehicles * 0.82],
    },
    {
      label: 'Maintenance',
      value: formatNumber(metrics.underMaintenance),
      helper: 'Service queue',
      icon: <ScooterIcon />,
      color: chartPalette.amber,
      soft: '#fef3c7',
      bars: [metrics.underMaintenance, metrics.underMaintenance * 0.64, metrics.underMaintenance * 0.38],
    },
  ]

  return (
<Box
  className="mobile-page management-dashboard-page"
  sx={(theme) => ({
    minHeight: '100vh',
    background: 'transparent',
    color: theme.palette.mode === 'dark' ? '#e5edf8' : '#0f172a',
    p: { xs: 0.5, sm: 1, md: 2 },
    pb: { xs: 2, md: 3 },
    overflowX: 'hidden',
  })}
>
<Box
  sx={{
    mb: { xs: 1, md: 1.25 },
    px: { xs: 0.5, md: 0 },
    pt: 0,
    minHeight: { xs: 70, md: 80 },
    position: 'relative',
    overflow: 'visible',
    border: '0',
    background: 'transparent',
    boxShadow: 'none',
    backdropFilter: 'none',
  }}
>
  <Box sx={{ position: 'relative', zIndex: 2 }}>
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      justifyContent="space-between"
      spacing={2.5}
      alignItems="flex-start"
    >
      {/* LEFT CONTENT */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, md: 2 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ width: '100%' }}
      >
        {/* TEXT */}
        <Box>
          <Typography
            variant="h4"
            sx={{
               fontFamily: "'Caveat', cursive",
              fontWeight: 950,
              lineHeight: 1.1,
              color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
              letterSpacing: 0,
              mb: 0.6,
              textShadow: theme.palette.mode === 'dark' ? '0 4px 16px rgba(0,0,0,0.35)' : 'none',
              fontSize: { xs: 24, md: 34 },
            }}
          >
            {getGreeting()}, {user?.firstName || 'Management'} 👋
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.mode === 'dark' ? 'rgba(229,237,248,0.82)' : '#475569',
              maxWidth: 700,
              fontWeight: 400,
              lineHeight: 1.45,
              fontSize: { xs: 13, md: 16 },
            }}
          >
            Live operational view across all departments.
          </Typography>
        </Box>
      </Stack>

      {/* RIGHT STATS */}
      <Stack
  direction="row"
  spacing={1}
  flexWrap={{ xs: 'wrap', md: 'nowrap' }}
  useFlexGap
  justifyContent={{ xs: 'center', md: 'flex-end' }}
  alignItems="center"
  sx={{
    width: { xs: '100%', md: 'auto' },
    mt: { xs: 2, md: 0 },
    gap: { xs: 0.75, md: 1 },
  }}
>
        {[
          {
            label: 'Riders',
            value: formatNumber(metrics.totalRiders),
            color: '#38bdf8',
          },
          {
            label: 'Fleet',
            value: formatNumber(metrics.totalFleetSize),
            color: '#22c55e',
          },
          {
            label: 'Orders',
            value: formatNumber(metrics.totalDeliveries),
            color: '#facc15',
          },
        ].map((item) => (
          <Box
            key={item.label}
            sx={{
              flex: { xs: '1 1 calc(33.333% - 8px)', sm: '0 0 auto' },
              minWidth: { xs: 0, sm: 84 },
              px: 1.2,
              py: 0.9,
              borderRadius: 2,
              background: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.58)' : 'rgba(255,255,255,0.78)',
              backdropFilter: 'blur(14px)',
              border: theme.palette.mode === 'dark' ? '1px solid rgba(148,163,184,0.20)' : '1px solid rgba(226,232,240,0.85)',
              boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
              transition: 'all .3s ease',
              textAlign: 'center',
              overflow: 'hidden',

              '&:hover': {
                transform: 'translateY(-3px)',
                background: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.72)' : 'rgba(255,255,255,0.92)',
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.mode === 'dark' ? 'rgba(229,237,248,0.72)' : '#475569',
                fontWeight: 600,
                fontSize: '11px',
              }}
            >
              {item.label}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: item.color,
                fontWeight: 800,
                mt: 0.3,
                fontSize: '15px',
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}

        {/* LAST UPDATED */}
        <Box
          sx={{
            px: 1.2,
            py: 0.9,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(34,197,94,0.95), rgba(16,185,129,0.86))',
            border: '1px solid rgba(255,255,255,0.18)',
            flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' },
            minWidth: { xs: 0, sm: 115 },
            textAlign: 'center',
            boxShadow: '0 10px 24px rgba(21,128,61,0.22)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)', fontWeight: 700, fontSize: '10px', display: 'block' }}>
            Last Updated
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '12px', display: 'block', mt: 0.3 }}>
            {lastUpdated
              ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : new Date(metrics.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>

        {/* REFRESH */}
        <IconButton
          onClick={() => {
            loadDashboardData()
            loadWarRoomLeaders()
          }}
          sx={{
            width: {
            xs: 40,
            md: 44,
            },
            height: {
            xs: 40,
            md: 44,
            },
            borderRadius: '12px',
            background: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.58)' : 'rgba(255,255,255,0.78)',
            color: theme.palette.mode === 'dark' ? '#e5edf8' : '#0f172a',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(148,163,184,0.20)' : '1px solid rgba(226,232,240,0.85)',
            flexShrink: 0,
            backdropFilter: 'blur(16px)',

            '&:hover': {
              background: theme.palette.mode === 'dark' ? 'rgba(14,165,233,0.25)' : 'rgba(224,242,254,0.95)',
              transform: 'rotate(180deg)',
            },

            transition: 'all .5s ease',
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Stack>
    </Stack>
  </Box>
</Box>

      <Grid container spacing={{ xs: 1.25, md: 3 }} mb={{ xs: 2, md: 3 }}>
        <Grid item xs={12} lg={7}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              minHeight: { xs: 'auto', md: 330 },
              borderRadius: 2,
              border: `1px solid ${panelBorder}`,
              background: panelSurface,
              color: panelText,
              backdropFilter: 'blur(18px)',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 28px 68px rgba(0, 0, 0, 0.34)'
                  : '0 28px 68px rgba(15, 23, 42, 0.13)',
              overflow: 'hidden',
              position: 'relative',
              '&:before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, rgba(17,24,39,0.72), rgba(11,17,32,0.74))`
                    : `linear-gradient(135deg, rgba(255,255,255,0.72), rgba(248,250,252,0.78))`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(2px)',
                transform: 'scale(1.02)',
                opacity: theme.palette.mode === 'dark' ? 0.15 : 0.20,
                pointerEvents: 'none',
              },
            })}
          >
            <CardContent sx={{ p: { xs: 1.5, md: 3 }, position: 'relative', zIndex: 1 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight={950}>
                    Fleet Energy Matrix
                  </Typography>
                  <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                    EV and IC count composition with city availability context.
                  </Typography>
                </Box>
                <Chip
                  label={`${formatPercentage(evMixRate)} EV mix`}
                  size="small"
                  sx={{ color: '#065f46', backgroundColor: '#dcfce7', border: '1px solid rgba(34,197,94,0.28)', fontWeight: 900 }}
                />
              </Stack>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      minHeight: { xs: 160, md: 250 },
                      borderRadius: 2,
                      background:
                        theme.palette.mode === 'dark'
                          ? 'linear-gradient(145deg, rgba(15,23,42,0.82), rgba(17,24,39,0.62))'
                          : 'linear-gradient(145deg, rgba(248,250,252,0.86), rgba(255,255,255,0.72))',
                      border: `1px solid ${panelBorder}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      perspective: '900px',
                      boxShadow: theme.palette.mode === 'dark'
                        ? 'inset 0 1px 0 rgba(255,255,255,0.05), 0 22px 40px rgba(0,0,0,0.26)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.78), 0 22px 40px rgba(15,23,42,0.10)',
                    }}
                  >
                    <Box
                      component="img"
                      src={evDashboardImage}
                      alt="EV91 dashboard placeholder"
                      sx={{
                        width: '100%',
                        maxWidth: 320,
                        height: 'auto',
                        display: 'block',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        imageRendering: 'auto',
                        mx: 'auto',
                      }}
                    />
                    {[].map((badge) => (
                      <Box
                        key={badge.label}
                        component={motion.div}
                        whileHover={{ y: -3, scale: 1.03 }}
                        sx={{
                          position: 'absolute',
                          ...badge.position,
                          p: 1,
                          minWidth: 74,
                          borderRadius: 1.5,
                          border: theme.palette.mode === 'dark' ? '1px solid rgba(148,163,184,0.20)' : '1px solid rgba(226,232,240,0.9)',
                          background: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.72)' : 'rgba(255,255,255,0.72)',
                          backdropFilter: 'blur(14px)',
                          boxShadow: theme.palette.mode === 'dark' ? '0 14px 28px rgba(0,0,0,0.28)' : '0 14px 28px rgba(15,23,42,0.12)',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: mutedText, fontWeight: 800, display: 'block', lineHeight: 1 }}>
                          {badge.label}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: panelText, fontWeight: 950, lineHeight: 1.1, mt: 0.4 }}>
                          {badge.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    {[
                      { label: 'EV Vehicles', value: metrics.evVehicleCount, color: chartPalette.emerald, cities: evCityData.length },
                      { label: 'IC Vehicles', value: metrics.icVehicleCount, color: chartPalette.cyan, cities: icCityData.length },
                    ].map((item) => (
                      <Grid item xs={12} sm={6} key={item.label}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            background: theme.palette.mode === 'dark'
                              ? 'linear-gradient(145deg, rgba(15,23,42,0.72), rgba(30,41,59,0.52))'
                              : 'linear-gradient(145deg, rgba(255,255,255,0.96), rgba(240,253,244,0.72))',
                            border: `1px solid ${panelBorder}`,
                            boxShadow: theme.palette.mode === 'dark' ? 'inset 0 1px 0 rgba(255,255,255,0.05), 0 14px 28px rgba(0,0,0,0.20)' : 'inset 0 1px 0 rgba(255,255,255,0.75), 0 14px 28px rgba(15,23,42,0.08)',
                          }}
                        >
                          <Typography variant="caption" sx={{ color: mutedText, fontWeight: 800 }}>
                            {item.label}
                          </Typography>
                          <Typography variant="h3" fontWeight={950} sx={{ color: item.color, mt: 1, lineHeight: 1 }}>
                            {formatNumber(item.value)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: mutedText }}>
                            {item.cities} city records
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <ThreeDBarScene
                      data={[
                        { name: 'EV', value: metrics.evVehicleCount, fill: chartPalette.emerald },
                        { name: 'IC', value: metrics.icVehicleCount, fill: chartPalette.cyan },
                        { name: 'Fleet', value: metrics.totalFleetSize, fill: modeAccent },
                      ]}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <IndiaMapCard
            cities={indiaMapCities}
            selectedCity={selectedOperationsCity || indiaMapCities[0]?.city || ''}
            selectedVehicle={selectedOperationsVehicle || indiaMapCities.find((city) => city.city === selectedOperationsCity)?.vehicleOptions[0] || ''}
            selectedRider={selectedOperationsRider || `All riders (${formatNumber(indiaMapCities.find((city) => city.city === selectedOperationsCity)?.riders || 0)})`}
            onCityChange={handleOperationsCityChange}
            onVehicleChange={setSelectedOperationsVehicle}
            onRiderChange={setSelectedOperationsRider}
          />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 1.25, md: 2 }} mb={{ xs: 2, md: 3 }}>
        {topMetricCards.map((item) => (
          <Grid item xs={6} sm={6} md={3} key={item.label}>
            <MetricPrismCard {...item} />
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : '#e0f2fe',
            color: '#0284c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <TrendingUpIcon />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Executive Graph Console
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live service performance translated into charts for fast comparison.
          </Typography>
        </Box>
      </Stack>

      {dashboardCharts && (
        <>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} lg={8}>
              <ChartPanel
                title="Command Trend"
                subtitle="Active output against total operating capacity"
                icon={<TrendingUpIcon />}
                action={<Chip label="Live" color="success" size="small" sx={{ fontWeight: 800 }} />}
              >
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dashboardCharts.commandTrend} margin={{ top: 12, right: 18, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="activeGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={modeAccent} stopOpacity={0.38} />
                          <stop offset="95%" stopColor={modeAccent} stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartPalette.emerald} stopOpacity={0.28} />
                          <stop offset="95%" stopColor={chartPalette.emerald} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={chartPalette.grid} vertical={false} />
                      <XAxis dataKey="name" tickLine={false} axisLine={false} tick={<CustomXAxisTick />} interval={0} />
                        <YAxis tickLine={false} axisLine={false} tickFormatter={(value) => formatNumber(Number(value))} width={54} />
                        <RechartsTooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="total" stroke={chartPalette.emerald} fill="url(#totalGradient)" strokeWidth={3} />
                      <Area type="monotone" dataKey="active" stroke={modeAccent} fill="url(#activeGradient)" strokeWidth={3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </ChartPanel>
            </Grid>

            <Grid item xs={12} lg={4}>
              <ChartPanel title="Operational Pulse" subtitle="Four headline readiness gauges" icon={<SpeedIcon />}>
                <Grid container spacing={{ xs: 1.25, sm: 1.5 }} justifyContent="center">
                  {dashboardCharts.healthGauges.map((item) => (
                    <Grid item xs={6} sm={6} key={item.label}>
                      <DonutGauge label={item.label} value={item.value} color={item.color} />
                    </Grid>
                  ))}
                </Grid>
              </ChartPanel>
            </Grid>
          </Grid>

          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <WarRoomPulseTable
              stats={warRoomStats}
              leaders={warRoomLeaders.length ? warRoomLeaders : [
                { label: 'Today Highest', vehicleId: 'NA', distanceKm: 0, source: 'War Room' },
                { label: 'Month Highest', vehicleId: 'NA', distanceKm: 0, source: 'War Room' },
              ]}
              loading={warRoomLoading}
            />
          </Box>

          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={5}>
              <ChartPanel title="3D Department Scale" subtitle="Cross-service comparison by current totals" icon={<DeliveryIcon />}>
                <ThreeDBarScene data={dashboardCharts.departmentBars} />
              </ChartPanel>
            </Grid>

            <Grid item xs={12} md={3.5}>
              <ChartPanel title="Rider Readiness" subtitle={`${formatNumber(metrics.activeRiders)} active riders`} icon={<PeopleIcon />}>
                <Box sx={{ height: 310 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={dashboardCharts.riderSplit} dataKey="value" nameKey="name" innerRadius={68} outerRadius={112} paddingAngle={4}>
                        {dashboardCharts.riderSplit.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value: number) => formatNumber(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </ChartPanel>
            </Grid>

            <Grid item xs={12} md={3.5}>
              <ChartPanel title="Fleet Status" subtitle={`${formatPercentage(fleetDeploymentRate)} deployed`} icon={<ScooterIcon />}>
                <Box sx={{ height: 310 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={dashboardCharts.fleetStatus} dataKey="value" nameKey="name" innerRadius={60} outerRadius={108} paddingAngle={3}>
                        {dashboardCharts.fleetStatus.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value: number) => formatNumber(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </ChartPanel>
            </Grid>
          </Grid>

          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={4}>
              <ChartPanel title="EV vs IC Mix" subtitle={`${formatPercentage(evMixRate)} EV contribution`} icon={<ElectricBoltIcon />}>
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={dashboardCharts.energyMix} dataKey="value" nameKey="name" outerRadius={100} label>
                        {dashboardCharts.energyMix.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value: number) => formatNumber(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </ChartPanel>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartPanel title="Top Delivery Cities" subtitle="City leaders by orders today" icon={<HubIcon />}>
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboardCharts.cityOrders} layout="vertical" margin={{ top: 8, right: 18, left: 20, bottom: 0 }}>
                      <CartesianGrid stroke={chartPalette.grid} horizontal={false} />
                      <XAxis type="number" hide />
                      <YAxis dataKey="city" type="category" tickLine={false} axisLine={false} width={92} />
                      <RechartsTooltip formatter={(value: number) => formatNumber(value)} />
                      <Bar dataKey="orders" fill={chartPalette.emerald} radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </ChartPanel>
            </Grid>

            <Grid item xs={12} md={4}>
              <ChartPanel title="Client Growth" subtitle={`${formatCurrency(metrics.totalRevenue || 0)} revenue signal`} icon={<StoreIcon />}>
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboardCharts.clientMomentum} margin={{ top: 8, right: 18, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={chartPalette.grid} vertical={false} />
                      <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                      <YAxis tickLine={false} axisLine={false} tickFormatter={(value) => formatNumber(Number(value))} width={54} />
                      <RechartsTooltip formatter={(value: number) => formatNumber(value)} />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {dashboardCharts.clientMomentum.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </ChartPanel>
            </Grid>
          </Grid>

          <Card
            elevation={0}
            sx={(theme) => ({
              borderRadius: 2,
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.24)' : 'divider',
              // backgroundColor: '#ffffff',
              color: '#0f172a',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 16px 38px rgba(0, 0, 0, 0.18)'
                  : '0 16px 38px rgba(15, 23, 42, 0.08)',
            })}
          >
          </Card>
        </>
      )}
    </Box>
  )
}
